import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks/redux';
import MediaLibraryDrawer from '../components/media-library/MediaLibraryDrawer';
import ImageCropper from '../shared/components/modals/ImageCropper';
import LeaveAdCreativeStepsModal from '../shared/components/modals/LeaveAdCreativeStepsModal';
import SideBar from './SideBar';
import type { IAuthResponse } from '@/auth/models/auth.response';
import { getCredentialsFromLS, getValueFromLs, setKeyValuePairToLS, refreshLSAfterAdCreation } from '@/shared/services/localstorage';
import { setFinishingCreationOfAd } from '@/editor/store/reducers/modals-drawers.slice';
import { useGetMeQuery } from '../services/http/users.api';
import useAppInitLanguage from '@/shared/hooks/app-init-hook';
import { setCompanyId } from '../store/reducers/property-info.slice';
import { EDITOR_LS_CONSTANTS } from '../shared/constants/editor-LS.constants';
import type { FC } from 'react';
import { useEffect, type JSX } from 'react';
import { setMobilePlacement } from '@/editor/store/reducers/creative-step.slice';
import { prefetchEditorRoutes } from '@/editor/routes/routes';

const EditorLayout: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const credentials: IAuthResponse | null = getCredentialsFromLS();

  useEffect(() => {
    prefetchEditorRoutes();
  }, []);

  const { data: userData, isSuccess: userDataSuccess } = useGetMeQuery(undefined, { skip: !credentials });

  useEffect(() => {
    if (userDataSuccess && getValueFromLs(EDITOR_LS_CONSTANTS.COMPANY_ID) === null && userData !== undefined) {
      dispatch(setCompanyId(userData?.companyExternalId!));
      dispatch(setMobilePlacement({ mobilePlacement: userData.mobilePlacements }));
      setKeyValuePairToLS(EDITOR_LS_CONSTANTS.COMPANY_ID, userData?.companyExternalId!);
    }
  }, [userData]);

  useAppInitLanguage(userDataSuccess);

  const modalStates = useAppSelector((state) => state.editorModalsDrawersReducer);
  const navigate = useNavigate();

  const handleClose = () => {
    refreshLSAfterAdCreation();
    dispatch(setFinishingCreationOfAd(false));
    navigate('/editor');
  };


  useEffect(() => {
    if (modalStates.finishingCreationOfAd) {
      handleClose();
    }
  }, [modalStates.finishingCreationOfAd]);

  return (
    <Box
      component="div"
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}>
      <SideBar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
        }}>
        <Container disableGutters={true} maxWidth={false} sx={{ minHeight: '100%' }}>
          <Outlet />
        </Container>
      </Box>

      <MediaLibraryDrawer />
      {modalStates.leaveAdCreationSteps.isOpened && <LeaveAdCreativeStepsModal />}
      {modalStates.imageCropper.isOpened && <ImageCropper />}
    </Box>
  );
};

export default EditorLayout;
